import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from '../../config';
import { jwtDecode } from 'jwt-decode';
import ModernNavbarPureCSS from "../../components/Navbar/ModernNavBarPureCSS";
import styles from "./ModernSubscription.module.css";

const ModernSubscriptionPage = () => {
  const [username, setUsername] = useState("");

  // Subscription Types (plans + PAYG options)
  const [subscriptions, setSubscriptions] = useState([]);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [advertisingOptions, setAdvertisingOptions] = useState([]);

  // Loading and errors for subscription types (pricing grid)
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Dashboard: Active subscription(s) and credits
  const [activeSubs, setActiveSubs] = useState([]);
  const [subLoading, setSubLoading] = useState(true);

  const [credits, setCredits] = useState({ socialCredits: 0, videoCredits: 0 });
  const [creditLoading, setCreditLoading] = useState(true);

  // One-time purchase button loading
  const [actionLoading, setActionLoading] = useState(false);

  // Helpers
  const precedence = (id) => (id === 3 ? 3 : id === 2 ? 2 : id === 1 ? 1 : 0);
  const getTypeId = (s) => s?.type?.subscriptionTypeId ?? s?.subscriptionTypeId;

  const formatDate = (d) => {
    if (!d) return "";
    try {
      const dt = new Date(d);
      if (isNaN(dt.getTime())) return d;
      return dt.toLocaleDateString();
    } catch {
      return d;
    }
  };

  const creditText = (val) => {
    if (val === -1) return "Unlimited";
    return String(val || 0);
  };

  // Fetch: Subscription Types (Plans + PAYG)
  const fetchSubscriptionTypes = async (businessId) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/Subscription/getSubscriptionTypes`, {
        params: { mtype: 'all', bizid: businessId },
      });
      const data = response.data || [];
      setSubscriptions(data);
    } catch (err) {
      console.error("Error fetching subscriptions: ", err);
      setError("Failed to load subscriptions. Please try again later.");
      setSubscriptions([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch: Active Subscription(s) for dashboard details
  const fetchActiveSubscription = async (token) => {
    setSubLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/Subscription/getActiveSubscription`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const arr = Array.isArray(res.data) ? res.data : res.data ? [res.data] : [];
      setActiveSubs(arr);
    } catch (e) {
      console.error("Error fetching active subscription(s):", e);
      setActiveSubs([]);
    } finally {
      setSubLoading(false);
    }
  };

  // Fetch: Credits for Pro plans (type 2/3)
  const fetchSubscriptionCredits = async (token) => {
    setCreditLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/Subscription/getCredits`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const snap = {
        socialCredits: typeof res.data?.socialCredits === "number" ? res.data.socialCredits : 0,
        videoCredits: typeof res.data?.videoCredits === "number" ? res.data.videoCredits : 0,
      };
      setCredits(snap);
    } catch (e) {
      console.error("Error fetching credits:", e);
      setCredits({ socialCredits: 0, videoCredits: 0 });
    } finally {
      setCreditLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    const bootstrap = async () => {
      if (token) {
        const decodeToken = jwtDecode(token);
        setUsername(decodeToken.UserName);
        const businessId = decodeToken.BusinessId;

        // Load pricing data (plans + PAYG)
        await fetchSubscriptionTypes(businessId);

        // Load dashboard data (active subs + credits)
        fetchActiveSubscription(token);
        fetchSubscriptionCredits(token);
      } else {
        // No token, stop spinners
        setIsLoading(false);
        setSubLoading(false);
        setCreditLoading(false);
      }
    };
    bootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Split subscription types into plans vs PAYG
  useEffect(() => {
    if (Array.isArray(subscriptions)) {
      const plans = subscriptions.filter((sub) => !sub.title?.toLowerCase().includes(" ad"));
      const ads = subscriptions.filter((sub) => sub.title?.toLowerCase().includes(" ad"));
      setSubscriptionPlans(plans);
      setAdvertisingOptions(ads);
    }
  }, [subscriptions]);

  // Derived: choose the primary active subscription (3 > 2 > 1)
  const coreActive = activeSubs.filter((s) => {
    const id = getTypeId(s);
    return id === 1 || id === 2 || id === 3;
  });
  const chosenSub = coreActive
    .slice()
    .sort((a, b) => {
      const pb = precedence(getTypeId(b));
      const pa = precedence(getTypeId(a));
      if (pb !== pa) return pb - pa;
      const db = b?.datePaid ? new Date(b.datePaid).getTime() : 0;
      const da = a?.datePaid ? new Date(a.datePaid).getTime() : 0;
      return db - da;
    })[0];
  const chosenTypeId = chosenSub ? getTypeId(chosenSub) : null;
  const fallbackChosenTypeId = !chosenTypeId ? (subscriptionPlans.find(p => p?.hasValidSubscription)?.subscriptionTypeId ?? null) : null;
  const effectiveChosenTypeId = chosenTypeId || fallbackChosenTypeId;
  const hasActive = !!effectiveChosenTypeId;
  const shouldShowPAYG = !hasActive || effectiveChosenTypeId === 1;

  const isPlanDisabled = (plan) => {
    const id = plan?.subscriptionTypeId;
    const eff = effectiveChosenTypeId;
    if (!id) return false;
    if (eff === 3) return id === 2 || id === 1;
    if (eff === 2) return id === 1;
    return false;
  };

  const formatSydneyDate = (date) => {
    if (!date) return "-";

    return new Intl.DateTimeFormat("en-AU", {
      timeZone: "Australia/Sydney",
      day: "2-digit",
      month: "short",
      year: "numeric"
    }).format(new Date(date));
  };

  const handlePurchase = async (title, stripeProductId, mySubscriptionTypeId) => {
    try {
      const token = localStorage.getItem("token");
      if (token) {
        const decodeToken = jwtDecode(token);
        const businessId = decodeToken.BusinessId;
        const response = await axios.post(`${API_BASE_URL}/api/StripeWebhook/create-checkout-session`, {
          priceId: stripeProductId,
          businessId: businessId,
          successUrl: `${window.location.origin}/subscription/success`,
          cancelUrl: `${window.location.origin}/subscription/cancel`,
          customerEmail: decodeToken.unique_name,
          subscriptionTypeId: mySubscriptionTypeId,
          title: title,
          mode: "subscription",
        });
        const { url } = response.data || {};
        if (url) window.location.href = url;
      }
    } catch (error) {
      console.error("Error creating checkout session: ", error);
    }
  };

  const handleOneTimePurchase = async (title, stripeProductId, productTypeId) => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      if (!token) return;

      const decodeToken = jwtDecode(token);
      const businessId = decodeToken?.BusinessId;
      const customerEmail = decodeToken?.unique_name || decodeToken?.email || "";

      const response = await axios.post(`${API_BASE_URL}/api/StripeWebhook/create-checkout-session`, {
        priceId: stripeProductId,
        businessId: businessId,
        successUrl: `${window.location.origin}/subscription/success`,
        cancelUrl: `${window.location.origin}/subscription/cancel`,
        customerEmail: customerEmail,
        subscriptionTypeId: productTypeId, 
        title: title,
        mode: "payment",
      });

      if (response?.data?.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Error creating one-time payment session: ", error);
    } finally {
      setActionLoading(false);
    }
  };

  const renderContent = () => {
    if (isLoading) return <div className={styles.loadingMessage}>Loading Subscriptions...</div>;
    if (error) return <div className={styles.errorMessage}>{error}</div>;
    if (subscriptionPlans.length === 0 && advertisingOptions.length === 0) {
      return <div className={styles.loadingMessage}>No subscription plans available at this time.</div>;
    }

    return (
      <div className={styles.pricingSection}>
        {/* PLANS GRID */}
        <div className={styles.pricingGrid}>
          {subscriptionPlans.map((plan, index) => {
            const isFeatured = plan.title === "Pro Pack";
            const disabledByTier = isPlanDisabled(plan);
            const isCurrentPlan = plan.hasValidSubscription;

            const taglines = {
              "Starter Pack": "For getting started",
              "Pro Pack": "Best for growing businesses",
              "Corporate Pack": "For established businesses",
            };
            const tagline = taglines[plan.title] || "";

            return (
              <div
                key={index}
                className={`${styles.pricingCard} ${isFeatured ? styles.featuredPlan : ""}`}
              >
                {isFeatured && <span className={styles.popularBadge}>Most Popular</span>}
                <h3>{plan.title}</h3>
                {tagline && <p className={styles.planTagline}>{tagline}</p>}
                <p className={styles.price}>${plan.price}<span>/mo</span></p>
                <ul>
                  {Array.isArray(plan.features) &&
                    plan.features.map((feature, fIndex) => (
                      <li key={fIndex}>✓ {feature.featureName}</li>
                    ))}
                </ul>
                <button
                  onClick={() => handlePurchase(plan.title, plan.stripeProductId, plan.subscriptionTypeId)}
                  className={styles.btnGoldShiny}
                  style={{ width: '100%', marginTop: 'auto' }}
                  disabled={isCurrentPlan || disabledByTier}
                >
                  {isCurrentPlan ? "Current Plan" : "Upgrade Now"}
                </button>
              </div>
            );
          })}
        </div>

        {/* COMPARISON TABLE */}
        <div className={styles.comparisonWrapper}>
            <h3 className={styles.sectionDivider}>Compare Features</h3>
            <div className={styles.tableResponsive}>
                <table className={styles.comparisonTable}>
                    <thead>
                        <tr>
                            <th style={{textAlign:'left'}}>Feature</th>
                            <th className={styles.colFree}>Basic (Free)</th>
                            <th className={styles.colPaid}>Starter / Pro</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr><td>Directory Listing</td><td className={styles.textMuted}>Bottom of List</td><td className={styles.textBold}>High Visibility</td></tr>
                        <tr><td>Job Leads</td><td className={styles.iconCross}>✘ None</td><td className={styles.iconCheck}>✔ Unlimited</td></tr>
                        <tr><td>View Customer Phone/Email</td><td className={styles.iconCross}>✘ Locked</td><td className={styles.iconCheck}>✔ Unlocked</td></tr>
                        <tr><td>Receive Direct Messages</td><td className={styles.iconCross}>✘ Locked</td><td className={styles.iconCheck}>✔ Unlimited</td></tr>
                        <tr><td>Project Management Tools</td><td className={styles.iconCross}>✘ No</td><td className={styles.iconCheck}>✔ Yes</td></tr>
                        <tr><td>Verified Badge</td><td className={styles.iconCross}>✘ No</td><td className={styles.iconCheck}>✔ Yes</td></tr>
                    </tbody>
                </table>
            </div>
        </div>

        {/* PAY AS YOU GO */}
        {shouldShowPAYG && advertisingOptions.length > 0 && (
          <div className={styles.payAsYouGoSection}>
            <h3 className={styles.sectionDivider}>Or Pay As You Go</h3>
            <div className={`${styles.pricingCard} ${styles.payAsYouGoCard}`}>
                <h3>Advertising Boosts</h3>
                <p className={styles.cardDescription}>
                    Don't want a subscription? You can purchase one-off advertising boosts on our Social Media Platform.
                </p>
                <ul>
                  {advertisingOptions.map((option) => (
                    <li key={option.id} style={{display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'10px'}}>
                        <span style={{flex:'1'}}>
                            <strong>{option.title}:</strong> <span style={{fontSize:'0.9em', color:'#666'}}>{option.description}</span>
                        </span>
                        <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
                            <span className={styles.priceDetail}>${option.price} <small>incl. GST</small></span>
                            <button
                                onClick={() => handleOneTimePurchase(option.title, option.stripeProductId, option.id)}
                                className={styles.btnSecondary}
                                style={{padding:'5px 15px', fontSize:'0.85rem', minWidth:'80px'}}
                                disabled={actionLoading}
                            >
                                {actionLoading ? "..." : "Buy"}
                            </button>
                        </div>
                    </li>
                  ))}
                </ul>
                <p className={styles.footnote}>* Ads are valid for one month.</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.pageContainer}>
      <ModernNavbarPureCSS UserName={username} prefix={""} TabActiveId={"subscription"} />

      {/* NEW GOLD HEADER */}
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <div>
            <h1 className={styles.headerTitle}>Subscriptions</h1>
            <p className={styles.headerSubtitle}>Manage your plans, purchases, and ad credits</p>
          </div>
        </div>
      </header>

      {/* DASHBOARD GRID */}
      <div className={styles.mainContainer}>
          <div className={styles.dashboardGrid}>
            
            {/* Active Subscription Card */}
            <div className={styles.card}>
              <div className={styles.cardHeader}>Active Subscriptions</div>
              <div className={styles.cardContent}>
                {subLoading ? (
                  <div className={styles.loadingMessage}>Loading subscriptions...</div>
                ) : !hasActive ? (
                  <div className={styles.loadingMessage}>
                    No active subscription found. Choose a plan below.
                  </div>
                ) : (
                  <div className={styles.activeSubCard}>
                      <div className={styles.activeSubRow}>
                        <div className={styles.activeSubField}>
                          <div className={styles.activeSubLabel}>Plan</div>
                          <div className={styles.activeSubValue}>
                            {chosenSub?.type?.title || "—"}
                          </div>
                          {chosenSub?.type?.description && (
                            <div className={styles.activeSubDesc}>{chosenSub.type.description}</div>
                          )}
                        </div>
                        <div className={styles.activeSubField}>
                          <div className={styles.activeSubLabel}>Price</div>
                          <div className={styles.activeSubValue}>
                            {chosenSub?.type?.price != null ? `$${Number(chosenSub.type.price).toFixed(2)}` : "—"}
                          </div>
                          {chosenSub?.type?.terms && (
                            <div className={styles.activeSubDesc}>{chosenSub.type.terms}</div>
                          )}
                        </div>
                        <div className={styles.activeSubField}>
                          <div className={styles.activeSubLabel}>Amount Paid</div>
                          <div className={styles.activeSubValue}>
                            {chosenSub?.amountPaid != null ? `$${Number(chosenSub.amountPaid).toFixed(2)}` : "—"}
                          </div>
                          <div className={styles.activeSubDesc}>
                            Date: {formatSydneyDate(chosenSub?.datePaid)}
                          </div>
                        </div>
                        <div className={styles.activeSubField}>
                          <div className={styles.activeSubLabel}>Expiry</div>
                          <div className={`${styles.activeSubValue} ${styles.activeSubExpiry}`}>
                            {formatSydneyDate(chosenSub?.expiry)}
                          </div>
                        </div>
                      </div>
                  </div>
                )}
              </div>
            </div>

            {/* Ad Credits Card */}
              <div className={styles.card}>
                <div className={styles.cardHeader}>Build Start Ad Credits</div>
                <div className={styles.cardContent}>
                  {!hasActive ? (
                    <div className={styles.loadingMessage}>
                      Subscribe to a plan to access ad credits.
                    </div>
                  ) : creditLoading ? (
                    <div className={styles.loadingMessage}>Loading credits...</div>
                  ) : (
                    <div>
                      <div className={styles.creditStatsRow}>
                          <div className={styles.creditStatBox}>
                             <div className={styles.creditStatLabel}>Social Posts</div>
                             <div className={styles.creditStatValue}>{creditText(credits.socialCredits)}</div>
                          </div>
                          <div className={styles.creditStatBox}>
                             <div className={styles.creditStatLabel}>Video Posts</div>
                             <div className={styles.creditStatValue}>{creditText(credits.videoCredits)}</div>
                          </div>
                      </div>

                      <div className={styles.creditButtonsRow}>
                        {advertisingOptions.map((option) => {

                          return (
                            <button
                              key={option.id}
                              className={styles.btnOutlineBlue}
                              onClick={() =>
                                handleOneTimePurchase(
                                  option.title,
                                  option.stripeProductId,
                                  option.subscriptionTypeId
                                )
                              }
                              disabled={actionLoading}
                            >
                              {actionLoading ? "Processing..." : `Buy ${option.title}`}
                            </button>
                          );
                        })}
                        {/* <button className={styles.btnOutlineBlue} onClick={() => handleOneTimePurchase('Social Ad Post','price_1SFTn6G80LX4ZpjihdOQAFMV', 4)} disabled={actionLoading}>
                            {actionLoading ? "Processing..." : "Buy Sponsored Social Post"}
                        </button>
                        <button className={styles.btnOutlineIndigo} onClick={() => handleOneTimePurchase('Social Video Ad','price_1SFTnUG80LX4ZpjiXdvIiz85', 5)} disabled={actionLoading}>
                            {actionLoading ? "Processing..." : "Buy Sponsored Video Post"}
                        </button> */}
                      </div>
                    </div>
                  )}
                </div>
              </div>
          </div>

          <h2 className={styles.sectionTitle}>Plans Designed for Growth</h2>
          <p className={styles.subheadline}>
            Choose a subscription that scales with your business. No hidden fees. Cancel anytime.
          </p>
          {renderContent()}
      </div>
    </div>
  );
};

export default ModernSubscriptionPage;